import React, { useState } from 'react';
import { FileSignature, Eye, Pencil, Trash2, Receipt, Search } from 'lucide-react';
import { useData } from '../context/DataContext';
import { computeTotals, fmtRM2, fmtDate, genId } from '../lib/store';
import { PageHeader, StatusBadge, EmptyState, Card } from '../components/Shared';
import { LineItemDialog, DocumentPreview } from '../components/DocumentDialog';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function Invoices() {
  const { invoices, receipts, company, getCustomer, getNextNumber, addItem, updateItem, removeItem, consumeNumber } = useData();
  const [edit, setEdit] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');

  const blank = () => ({
    id: null, number: getNextNumber(company.invoicePrefix, 'invoices'),
    customerId: '', date: new Date().toISOString().slice(0, 10),
    dueDate: '', status: 'unpaid', items: [{ productId: '', name: '', qty: 1, price: 0, taxable: true }], amountPaid: 0,
  });

  const openNew = () => { setEdit(blank()); setEditOpen(true); };
  const openEdit = (item) => { setEdit(item); setEditOpen(true); };

  const save = (form) => {
    if (form.id) { updateItem('invoices', form.id, form); toast.success('Invoice updated'); }
    else { addItem('invoices', form); consumeNumber('invoices', form.number); toast.success('Invoice created'); }
    setEditOpen(false);
  };

  const markPaid = (item) => {
    const totals = computeTotals(item.items, company.sstRate);
    const outstanding = Math.max(0, totals.total - Number(item.amountPaid || 0));
    if (outstanding <= 0) {
      updateItem('invoices', item.id, { status: 'paid', amountPaid: totals.total });
      toast.success('Invoice already fully paid — marked as paid');
      return;
    }
    updateItem('invoices', item.id, { status: 'paid', amountPaid: totals.total });
    const receiptNumber = getNextNumber(company.receiptPrefix, 'receipts');
    addItem('receipts', {
      id: genId(), number: receiptNumber,
      customerId: item.customerId, invoiceNumber: item.number,
      date: new Date().toISOString().slice(0, 10), amount: outstanding, method: 'Bank Transfer',
    });
    consumeNumber('receipts', receiptNumber);
    toast.success(`Marked paid — receipt issued for ${fmtRM2(outstanding)}`);
  };

  const filtered = invoices
    .filter((x) => tab === 'all' || x.status === tab)
    .filter((x) => (getCustomer(x.customerId)?.name || '').toLowerCase().includes(q.toLowerCase()) || (x.number || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Invoices" subtitle="Create from scratch or convert from quotations." actionLabel="New Invoice" onAction={openNew} />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {['all', 'unpaid', 'partial', 'paid', 'overdue'].map((t) => (
              <TabsTrigger key={t} value={t} className="capitalize">{t}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={FileSignature} title="No invoices" subtitle="Create your first invoice." />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Number</TableHead>
                <TableHead className="font-semibold text-slate-600">Customer</TableHead>
                <TableHead className="font-semibold text-slate-600">Due</TableHead>
                <TableHead className="font-semibold text-slate-600">Status</TableHead>
                <TableHead className="text-right font-semibold text-slate-600">Total</TableHead>
                <TableHead className="text-right font-semibold text-slate-600">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => {
                const totals = computeTotals(item.items, company.sstRate);
                return (
                  <TableRow key={item.id} className="hover:bg-slate-50">
                    <TableCell className="font-semibold text-violet-600">{item.number || '—'}</TableCell>
                    <TableCell className="text-slate-700">{getCustomer(item.customerId)?.name || '—'}</TableCell>
                    <TableCell className="text-slate-500">{fmtDate(item.dueDate)}</TableCell>
                    <TableCell><StatusBadge status={item.status} /></TableCell>
                    <TableCell className="text-right font-semibold text-slate-800">{fmtRM2(totals.total)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-0.5">
                        <button title="Preview" onClick={() => setPreview(item)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Eye className="h-4 w-4" /></button>
                        <button title="Edit" onClick={() => openEdit(item)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                        {item.status !== 'paid' && <button title="Mark Paid & Issue Receipt" onClick={() => markPaid(item)} className="rounded-md p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"><Receipt className="h-4 w-4" /></button>}
                        <button title="Delete" onClick={() => { removeItem('invoices', item.id); toast.success('Deleted'); }} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <LineItemDialog open={editOpen} onOpenChange={setEditOpen} initial={edit} onSave={save}
        title={edit?.id ? 'Edit Invoice' : 'New Invoice'} extraDates={{ label: 'Due Date', field: 'dueDate' }} />
      <DocumentPreview open={!!preview} onOpenChange={(v) => !v && setPreview(null)} doc={preview} type="invoice" />
    </div>
  );
}
